import React, { useEffect, useState } from 'react';
import { getShows, getFeed } from '../services/contentService';
import { Show, Episode, ContentType, MediaType } from '../types';
import { useMedia } from '../context/MediaContext';
import { Play, Mic2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';

export const AudioHub: React.FC = () => {
  const [shows, setShows] = useState<Show[]>([]);
  const [latestEpisodes, setLatestEpisodes] = useState<Episode[]>([]);
  const { play } = useMedia();

  useEffect(() => {
    getShows('audio').then(setShows);
    getFeed().then(feed => {
      // Filter only audio episodes
      const episodes = (feed.filter(item => item.type === ContentType.Episode) as Episode[])
        .filter(e => e.mediaType === MediaType.Audio);
      setLatestEpisodes(episodes);
    });
  }, []);

  return (
    <div className="animate-fade-in">
      <SEOHead
        title="Audio Hub"
        description="Listen to exclusive podcasts, deep-dive interviews, and immersive sonic reporting from Nida Studios."
        type="website"
      />
      <div className="flex items-center gap-3 mb-8">
        <div className="p-3 bg-brand-100 rounded-full text-brand-400">
          <Mic2 size={24} />
        </div>
        <div>
          <h1 className="text-2xl font-sans font-black text-brand-900 uppercase tracking-tight">Nida Audio</h1>
          <p className="text-gray-500 text-xs font-medium">Deep dives, interviews, and sonic storytelling.</p>
        </div>
      </div>

      {/* Featured Shows Carousel (Grid for now) */}
      <section className="mb-12">
        <h3 className="text-lg font-bold text-brand-900 uppercase tracking-widest mb-8 border-b border-gray-100 pb-4">Recent Documentaries</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {shows.map(show => (
            <Link to={`/audio/show/${show.slug || show.id}`} key={show.id} className="group cursor-pointer">
              <div className="aspect-[4/3] rounded-xl overflow-hidden mb-3 shadow-md">
                <img src={show.coverImage} alt={show.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <h3 className="text-sm font-bold text-gray-900 leading-tight group-hover:text-brand-400 transition-colors">{show.title}</h3>
              <p className="text-[10px] text-gray-500 mt-1">{show.host.name}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Latest Episodes List */}
      <section>
        <h2 className="text-base font-bold uppercase tracking-widest mb-6 border-b border-gray-100 pb-2">Latest Episodes</h2>
        <div className="space-y-4">
          {latestEpisodes.map(ep => (
            <div key={ep.id} className="flex gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors group cursor-pointer border border-transparent hover:border-gray-100" onClick={() => play(ep)}>
              <div className="relative w-20 h-20 md:w-28 md:h-28 flex-shrink-0 rounded-lg overflow-hidden">
                <img src={ep.thumbnail} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 flex items-center justify-center transition-colors">
                  <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md">
                    <Play size={14} className="ml-1 fill-brand-900 text-brand-900" />
                  </div>
                </div>
              </div>
              <div className="flex-1 min-w-0 flex flex-col justify-center">
                <span className="text-[10px] font-bold text-brand-400 uppercase mb-1">Episode {ep.episodeNumber}</span>
                <h3 className="text-sm md:text-lg font-bold text-brand-900 mb-1 truncate group-hover:text-brand-400 transition-colors">{ep.title}</h3>
                <p className="text-xs text-gray-500 line-clamp-2 mb-2 hidden md:block">{ep.description}</p>
                <div className="text-xs text-gray-400">
                  {Math.floor(ep.duration / 60)} min • {new Date(ep.publishedAt).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};